import React from 'react';
import AppliedFilterDisplayChip from 'src/App/Dashboards/Market/Components/Chips/AppliedFilterDisplayChip';
import { DEFAULT_FILTER_VALUES, exchangeRates } from 'src/utils/const';
import { AmenitiesIcons } from 'src/utils/icons';
import { FilterVal } from 'src/interfaces/iproptypes';
import { abbreviateNumber } from 'src/utils/functions';

const AppliedFiltersDisplay = ({
  filterVal,
  setFilterVal,
  toggleDrawer,
  filterActive,
  currency,
}: {
  filterVal: FilterVal;
  setFilterVal: (val: any) => void;
  toggleDrawer: () => void;
  filterActive: boolean;
  currency: string;
}) => {
  const getFilterChips = () => {
    const chips: Array<{
      label: string;
      value: string;
      key: string;
      emoji?: JSX.Element;
    }> = [];
    const exchangeSymbol = exchangeRates[currency].symbol;

    // Price tier filter
    if (filterVal.priceTierFilter.length > 0 && filterVal.priceTierFilter[0] !== 'all') {
      const label = filterVal.priceTierFilter
        .map((tier: any) => {
          return `${tier[0].toUpperCase()}${tier.slice(1)}`;
        })
        .join(', ');

      if (label) {
        chips.push({
          label,
          value: 'priceTierFilter',
          key: 'priceTierFilter',
        });
      }
    }

    // Revenue filter
    if (filterVal.revenueFilter.length > 0 && filterVal.revenueFilter[0] !== 'all') {
      const label =
        (filterVal.revenueFilter as number[])
          .sort((a: number, b: number) => a - b)
          .map((tier: any) => {
            return `${tier}th`;
          })
          .filter(Boolean)
          .join(', ') + ' Percentile';

      if (label) {
        chips.push({
          label,
          value: 'revenueFilter',
          key: 'revenueFilter',
        });
      }
    }

    // Bedrooms filter
    if (filterVal.bedrooms.length > 0 && !filterVal.bedrooms.includes(100)) {
      chips.push({
        label: `🛏️ ${filterVal.bedrooms.join(',')}`,
        value: 'bedrooms',
        key: 'bedrooms',
      });
    }

    // Bathrooms filter
    if (filterVal.bathrooms.length > 0 && !filterVal.bathrooms.includes(100)) {
      chips.push({
        label: `🚿 ${filterVal.bathrooms.join(',')}`,
        value: 'bathrooms',
        key: 'bathrooms',
      });
    }

    // Accommodates filter
    if (
      filterVal.accommodates &&
      (filterVal.accommodates[0] > 1 || filterVal.accommodates[1] < 16)
    ) {
      chips.push({
        label: `👥 ${filterVal.accommodates[0]}-${filterVal.accommodates[1]}`,
        value: 'accommodates',
        key: 'accommodates',
      });
    }

    // Minimum nights filter
    if (
      filterVal.minimum_nights &&
      (filterVal.minimum_nights[0] > 1 || filterVal.minimum_nights[1] < 30)
    ) {
      chips.push({
        label: `🌙 ${filterVal.minimum_nights[0]}-${filterVal.minimum_nights[1]}`,
        value: 'minimum_nights',
        key: 'minimum_nights',
      });
    }

    // Number of reviews filter
    if (filterVal.number_of_reviews !== 'all') {
      chips.push({
        label: `📝 ${filterVal.number_of_reviews}+`,
        value: 'number_of_reviews',
        key: 'number_of_reviews',
      });
    }

    // Rating filter
    if (filterVal.rating && (filterVal.rating[0] > 3.9 || filterVal.rating[1] < 5)) {
      chips.push({
        label: `⭐ ${filterVal.rating[0]}-${filterVal.rating[1]}`,
        value: 'rating',
        key: 'rating',
      });
    }

    // Host type filter
    if (filterVal.hostType !== 'both') {
      const label =
        filterVal.hostType === 'professional'
          ? '🏢 Professionally managed'
          : '🏠 Independently managed';

      chips.push({
        label,
        value: 'hostType',
        key: 'hostType',
      });
    }

    // Amenities filter

    Object.entries(filterVal.amenities).forEach(([amenity, value]) => {
      if (value !== null) {
        const amenityName = amenity.replace('_', ' ');
        const emoji: JSX.Element = AmenitiesIcons[amenity];
        const label = value === true ? `${amenityName}` : `No ${amenityName}`;

        chips.push({
          label,
          value: `amenities-${amenity}`,
          key: `amenities-${amenity}`,
          emoji,
        });
      }
    });

    // Property Type

    if (filterVal.roomType.length > 0 && filterVal.roomType[0] !== 'all') {
      const label = filterVal.roomType.map((roomType: string) => {
        return roomType.replace('_', ' ');
      });
      chips.push({
        label: `🏠 ${label.join(',')}`,
        value: 'roomType',
        key: 'roomType',
      });
    }

    return chips;
  };

  const handleRemoveFilter = (key: string) => {
    const newFilterVal = { ...filterVal };

    if (key === 'priceTierFilter') {
      newFilterVal.priceTierFilter = [...DEFAULT_FILTER_VALUES.priceTierFilter];
    } else if (key === 'revenueFilter') {
      newFilterVal.revenueFilter = [...DEFAULT_FILTER_VALUES.revenueFilter];
    } else if (key === 'bedrooms') {
      newFilterVal.bedrooms = [...DEFAULT_FILTER_VALUES.bedrooms];
    } else if (key === 'bathrooms') {
      newFilterVal.bathrooms = [...DEFAULT_FILTER_VALUES.bathrooms];
    } else if (key === 'accommodates') {
      newFilterVal.accommodates = [...DEFAULT_FILTER_VALUES.accommodates];
    } else if (key === 'minimum_nights') {
      newFilterVal.minimum_nights = [...DEFAULT_FILTER_VALUES.minimum_nights];
    } else if (key === 'number_of_reviews') {
      newFilterVal.number_of_reviews = DEFAULT_FILTER_VALUES.number_of_reviews;
    } else if (key === 'rating') {
      newFilterVal.rating = [...DEFAULT_FILTER_VALUES.rating];
    } else if (key === 'hostType') {
      newFilterVal.hostType = DEFAULT_FILTER_VALUES.hostType as 'both';
    } else if (key.startsWith('amenities-')) {
      const amenity = key.split('-')[1];
      // ensure we dont pass empty object to the api
      if (typeof filterVal.amenities[amenity] === 'boolean') {
        newFilterVal.amenities[amenity] = DEFAULT_FILTER_VALUES.amenities[amenity];
      }
    } else if (key === 'roomType') {
      newFilterVal.roomType = [...DEFAULT_FILTER_VALUES.roomType];
    }

    setFilterVal(newFilterVal);
  };

  const chips = getFilterChips();

  if (chips.length === 0 || !filterActive) {
    return null;
  }

  return (
    <div
      style={{
        zIndex: 999,
        display: 'flex',
        flexWrap: 'wrap',
        gap: '8px',
        maxWidth: '50%',
        padding: '5px',
        pointerEvents: 'auto',
      }}
    >
      {chips.map(chip => (
        <AppliedFilterDisplayChip
          key={chip.key}
          chip={chip}
          toggleDrawer={toggleDrawer}
          handleRemoveFilter={handleRemoveFilter}
        />
      ))}
    </div>
  );
};

export default AppliedFiltersDisplay;
